import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useBranding } from '../../contexts/BrandingContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiToggleLeft, FiToggleRight, FiPlus, FiTrash2, FiCalendar, FiCheck, FiClock, FiLayout } = FiIcons;

const ALL_SLOTS = [
  '09:00 AM - 12:00 PM',
  '12:00 PM - 03:00 PM',
  '03:00 PM - 06:00 PM',
  '06:00 PM - 09:00 PM',
];

export default function Settings() {
  const { refreshBranding } = useBranding();
  
  // Timeslot State
  const [config, setConfig] = useState({
    enabled: true,
    active_slots: [...ALL_SLOTS],
    universal_slot: '09:00 AM - 08:00 PM',
  });
  
  // Holidays State
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: '', message: '' });
  
  // Branding & SEO State
  const [seoBranding, setSeoBranding] = useState({
    app_name: '',
    seo_title: '',
    seo_description: '',
    logo_icon: 'Droplet',
    logo_url: ''
  });

  // UI State
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingHoliday, setSavingHoliday] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [savedConfig, setSavedConfig] = useState(false);
  const [savedBranding, setSavedBranding] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('app_settings')
      .select('key, value')
      .in('key', ['timeslot_config', 'holidays', 'seo_branding']);

    if (data) {
      const cfg = data.find((d) => d.key === 'timeslot_config');
      const hols = data.find((d) => d.key === 'holidays');
      const branding = data.find((d) => d.key === 'seo_branding');

      if (cfg) setConfig(cfg.value);
      if (hols) setHolidays(hols.value || []);
      if (branding) setSeoBranding(branding.value);
    }
    setLoading(false);
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    const { error } = await supabase
      .from('app_settings')
      .upsert({ key: 'timeslot_config', value: config }, { onConflict: 'key' });
    
    setSavingConfig(false);
    if (!error) {
      setSavedConfig(true);
      setTimeout(() => setSavedConfig(false), 2000);
    }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    const { error } = await supabase
      .from('app_settings')
      .upsert({ key: 'seo_branding', value: seoBranding }, { onConflict: 'key' });
    
    setSavingBranding(false);
    if (!error) {
      refreshBranding(); // Update globally immediately
      setSavedBranding(true);
      setTimeout(() => setSavedBranding(false), 2000);
    }
  };

  const toggleSlot = (slot) => {
    setConfig((prev) => {
      const active = prev.active_slots.includes(slot)
        ? prev.active_slots.filter((s) => s !== slot)
        : [...prev.active_slots, slot];
      return { ...prev, active_slots: active };
    });
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.date || !newHoliday.message) return;
    const exists = holidays.find((h) => h.date === newHoliday.date);
    if (exists) return;

    setSavingHoliday(true);
    const updated = [...holidays, { date: newHoliday.date, message: newHoliday.message }]
      .sort((a, b) => a.date.localeCompare(b.date));

    const { error } = await supabase
      .from('app_settings')
      .upsert({ key: 'holidays', value: updated }, { onConflict: 'key' });

    if (!error) {
      setHolidays(updated);
      setNewHoliday({ date: '', message: '' });
    }
    setSavingHoliday(false);
  };

  const handleRemoveHoliday = async (date) => {
    const updated = holidays.filter((h) => h.date !== date);
    await supabase
      .from('app_settings')
      .upsert({ key: 'holidays', value: updated }, { onConflict: 'key' });
    setHolidays(updated);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        {[1, 2, 3].map((i) => <div key={i} className="h-32 bg-gray-200 animate-pulse rounded-2xl" />)}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-brand-navy">Application Settings</h1>
      </div>

      {/* SEO & Branding Settings */}
      <Card>
        <h2 className="font-bold text-brand-navy text-lg flex items-center gap-2 mb-5">
          <FiLayout className="text-brand-gold" /> Branding & SEO
        </h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="App Name"
              value={seoBranding.app_name}
              onChange={(e) => setSeoBranding({ ...seoBranding, app_name: e.target.value })}
            />
            <Input
              label="Logo Icon (Feather Icon Name)"
              placeholder="Droplet"
              value={seoBranding.logo_icon}
              onChange={(e) => setSeoBranding({ ...seoBranding, logo_icon: e.target.value })}
            />
          </div>
          
          <Input
            label="Logo / Favicon URL (Optional, overrides icon)"
            placeholder="https://example.com/logo.png"
            value={seoBranding.logo_url}
            onChange={(e) => setSeoBranding({ ...seoBranding, logo_url: e.target.value })}
          />

          <div className="border-t border-gray-100 pt-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Search Engine Optimization</h3>
            <div className="space-y-3">
              <Input
                label="SEO Page Title"
                placeholder="e.g. Mercury Dry Cleaners - Professional Garment Care"
                value={seoBranding.seo_title}
                onChange={(e) => setSeoBranding({ ...seoBranding, seo_title: e.target.value })}
              />
              <Input
                label="SEO Description"
                placeholder="Brief description of your service for search engines"
                value={seoBranding.seo_description}
                onChange={(e) => setSeoBranding({ ...seoBranding, seo_description: e.target.value })}
              />
            </div>
          </div>

          <Button 
            onClick={handleSaveBranding} 
            isLoading={savingBranding} 
            variant="accent" 
            className="w-full mt-4"
          >
            {savedBranding ? '✓ Saved!' : 'Save Branding & SEO'}
          </Button>
        </div>
      </Card>

      {/* Time Slot Toggle */}
      <Card>
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="font-bold text-brand-navy text-lg flex items-center gap-2">
              <FiClock className="text-brand-gold" /> Time Slot Mode
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {config.enabled ? 'Customers choose from specific time windows' : 'All-day slot shown: ' + config.universal_slot}
            </p>
          </div>
          <button
            onClick={() => setConfig((p) => ({ ...p, enabled: !p.enabled }))}
            className="text-4xl transition-colors"
          >
            {config.enabled ? <FiToggleRight className="text-brand-gold" /> : <FiToggleLeft className="text-gray-400" />}
          </button>
        </div>

        {config.enabled ? (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Active Slots (check to enable)
            </p>
            <div className="space-y-2">
              {ALL_SLOTS.map((slot) => {
                const active = config.active_slots.includes(slot);
                return (
                  <button
                    key={slot}
                    onClick={() => toggleSlot(slot)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all ${
                      active ? 'border-brand-gold bg-brand-gold/5 text-brand-navy' : 'border-gray-200 bg-gray-50 text-gray-400'
                    }`}
                  >
                    <span className="font-medium text-sm">{slot}</span>
                    <div className={`h-5 w-5 rounded-full flex items-center justify-center ${active ? 'bg-brand-gold' : 'bg-gray-200'}`}>
                      {active && <FiCheck className="text-white text-xs" />}
                    </div>
                  </button>
                );
              })}
            </div>
            {config.active_slots.length === 0 && (
              <p className="text-xs text-red-500 mt-2">⚠ At least one slot must be active</p>
            )}
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
            <p className="text-sm text-brand-navy font-medium">Universal Slot Active</p>
            <p className="text-2xl font-bold text-brand-gold mt-1">{config.universal_slot}</p>
            <p className="text-xs text-gray-500 mt-1">All customers will book for this window</p>
          </div>
        )}

        <Button
          onClick={handleSaveConfig}
          isLoading={savingConfig}
          disabled={config.enabled && config.active_slots.length === 0}
          variant="outline"
          className="w-full mt-5"
        >
          {savedConfig ? '✓ Saved!' : 'Save Time Slot Settings'}
        </Button>
      </Card>

      {/* Holidays */}
      <Card>
        <h2 className="font-bold text-brand-navy text-lg flex items-center gap-2 mb-5">
          <FiCalendar className="text-brand-gold" /> Holiday Management
        </h2>
        <div className="flex gap-2 mb-5">
          <input
            type="date"
            className="flex-1 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold"
            value={newHoliday.date}
            onChange={(e) => setNewHoliday((p) => ({ ...p, date: e.target.value }))}
          />
          <input
            type="text"
            placeholder="e.g. Diwali"
            className="flex-1 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold"
            value={newHoliday.message}
            onChange={(e) => setNewHoliday((p) => ({ ...p, message: e.target.value }))}
          />
          <Button
            onClick={handleAddHoliday}
            isLoading={savingHoliday}
            disabled={!newHoliday.date || !newHoliday.message}
            variant="accent"
            size="sm"
            className="shrink-0"
          >
            <FiPlus />
          </Button>
        </div>

        {holidays.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
            <SafeIcon name="Calendar" className="text-3xl text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No holidays added yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {holidays.map((h) => (
              <div key={h.date} className="flex items-center justify-between px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
                <div>
                  <p className="font-semibold text-red-700 text-sm">{h.message}</p>
                  <p className="text-xs text-red-400">
                    {new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                <button
                  onClick={() => handleRemoveHoliday(h.date)}
                  className="p-2 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors"
                >
                  <FiTrash2 />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}